import { useMutation } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router';

import { AuthScreen, SubmitButton } from '@/components/AuthScreen';
import { Field } from '@/components/Field';
import { useT } from '@/i18n/context';
import { ApiError, resetPassword } from '@/lib/api';

/**
 * Landing page for the link in the reset email.
 *
 * The backend points that link here rather than at an API route: the API is
 * stateless JSON and has no page to render (see `configurePasswordReset` in
 * AppServiceProvider).
 */
export function ResetPasswordPage() {
  const t = useT();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const emailFromLink = params.get('email') ?? '';

  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');

  const reset = useMutation({ mutationFn: resetPassword });
  const failure = reset.error instanceof ApiError ? reset.error : null;
  const fieldErrors = failure?.fieldErrors ?? {};

  const submit = (event: FormEvent) => {
    event.preventDefault();

    reset.mutate({
      token,
      email: emailFromLink,
      password,
      password_confirmation: confirmation,
    });
  };

  if (!token || !emailFromLink) {
    return (
      <AuthScreen
        title={t('auth.link_incomplete_title')}
        error={t('auth.link_incomplete')}
        footer={
          <Link to="/forgot-password" className="underline">
            {t('auth.request_new_link')}
          </Link>
        }
      >
        <span />
      </AuthScreen>
    );
  }

  return (
    <AuthScreen
      title={t('auth.reset_title')}
      subtitle={emailFromLink}
      error={failure && failure.status !== 422 ? failure.message : null}
      footer={
        <Link to="/login" className="underline">
          {t('auth.back_to_sign_in')}
        </Link>
      }
    >
      {reset.isSuccess ? (
        <p role="status" className="rounded-lg bg-current/5 p-4 text-sm">
          {reset.data.message} {t('auth.reset_done')}
        </p>
      ) : (
        <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
          {/* Surfaced here because the API reports an expired or reused token as
              a validation error on `email`, which has no field on this form. */}
          {fieldErrors.email && (
            <p role="alert" className="rounded-lg bg-red-500/10 p-4 text-sm text-red-500">
              {fieldErrors.email}
            </p>
          )}

          <Field
            label={t('auth.new_password')}
            type="password"
            name="password"
            autoComplete="new-password"
            required
            value={password}
            error={fieldErrors.password}
            onChange={(event) => setPassword(event.target.value)}
          />

          <Field
            label={t('auth.confirm_password')}
            type="password"
            name="password_confirmation"
            autoComplete="new-password"
            required
            value={confirmation}
            onChange={(event) => setConfirmation(event.target.value)}
          />

          <SubmitButton pending={reset.isPending}>
            {reset.isPending ? t('auth.saving') : t('auth.save_password')}
          </SubmitButton>
        </form>
      )}
    </AuthScreen>
  );
}
